import { cn } from "@/modern/utils";
import type { BucketStatus, BucketFill, ProblemHub, BestNextWin, SequenceStatus } from "../types";

// ---------------------------------------------------------------------------
// Coverage status helpers
// ---------------------------------------------------------------------------

export function getBucketStatus(count: number): BucketStatus {
  if (count === 0) return "empty";
  if (count === 1) return "partial";
  if (count <= 3) return "ready";
  return "strong";
}

export function getHubStatus(buckets: BucketFill[]): BucketStatus {
  const total = buckets.length;
  if (total === 0) return "empty";
  const filled = buckets.filter((b) => b.assetCount > 0).length;
  const ratio = filled / total;
  if (ratio >= 1) return "strong";
  if (ratio >= 0.8) return "ready";
  if (ratio >= 0.4) return "partial";
  return "empty";
}

export function getSequenceStatus(
  beforeCount: number,
  duringCount: number,
  afterCount: number,
): SequenceStatus {
  const hasBefore = beforeCount > 0;
  const hasDuring = duringCount > 0;
  const hasAfter = afterCount > 0;

  if (hasBefore && hasAfter && hasDuring) return "sequence_ready";
  if (hasBefore && hasAfter) return "before_after_available";
  if (!hasBefore && hasAfter) return "missing_before";
  if (hasBefore && !hasAfter) return "missing_after";
  return "no_sequence";
}

function hubTemporalTotals(hub: ProblemHub): { before: number; during: number; after: number } {
  return hub.buckets.reduce(
    (acc, b) => ({
      before: acc.before + b.beforeCount,
      during: acc.during + b.duringCount,
      after: acc.after + b.afterCount,
    }),
    { before: 0, during: 0, after: 0 },
  );
}

function contentGapCount(hub: ProblemHub): number {
  return hub.buckets.filter((b) => b.assetCount === 0).length;
}

function sequenceGapCount(totals: { before: number; during: number; after: number }): number {
  return (totals.before === 0 ? 1 : 0) + (totals.during === 0 ? 1 : 0) + (totals.after === 0 ? 1 : 0);
}

function primaryBestNextAction(
  hub: ProblemHub,
  totals: { before: number; during: number; after: number },
): { label: string; gapType: "content" | "sequence" } {
  const emptyBucket = hub.buckets.find((b) => b.assetCount === 0);
  if (emptyBucket) return { label: emptyBucket.label, gapType: "content" };

  if (totals.before === 0) return { label: "Missing before", gapType: "sequence" };
  if (totals.after === 0) return { label: "Missing after", gapType: "sequence" };
  if (totals.during === 0) return { label: "Missing during", gapType: "sequence" };
  return { label: "No sequence", gapType: "sequence" };
}

export function computeBestNextWins(hubs: ProblemHub[]): BestNextWin[] {
  type Scored = {
    totalScore: number;
    contentGaps: number;
    fillRatio: number;
    name: string;
    win: BestNextWin;
  };

  const scored: Scored[] = [];

  for (const hub of hubs) {
    const totals = hubTemporalTotals(hub);
    const contentGaps = contentGapCount(hub);
    const sequenceGaps = sequenceGapCount(totals);
    const totalScore = contentGaps + sequenceGaps;
    if (totalScore <= 0) continue;

    const tr = Math.max(hub.totalRequired, 1);
    const fillRatio = hub.filledRequired / tr;
    const { label, gapType } = primaryBestNextAction(hub, totals);

    scored.push({
      totalScore,
      contentGaps,
      fillRatio,
      name: hub.name,
      win: {
        hubId: hub.id,
        hubName: hub.name,
        missingBucket: label,
        filledFraction: `${hub.filledRequired}/${hub.totalRequired}`,
        gapType,
      },
    });
  }

  scored.sort((a, b) => {
    if (a.totalScore !== b.totalScore) return a.totalScore - b.totalScore;
    if (a.contentGaps !== b.contentGaps) return a.contentGaps - b.contentGaps;
    if (b.fillRatio !== a.fillRatio) return b.fillRatio - a.fillRatio;
    return a.name.localeCompare(b.name);
  });

  return scored.slice(0, 5).map((s) => s.win);
}

// ---------------------------------------------------------------------------
// Badge style constants & helpers
// ---------------------------------------------------------------------------

export const COVERAGE_BADGE_BASE = "!text-2xs !py-0.5 !border-transparent";

// Hub-level status is based on the ratio of filled roles (getHubStatus),
// not per-asset counts — these tooltips must reflect that.
export const BUCKET_STATUS_TOOLTIPS: Record<BucketStatus, string> = {
  empty: "Fewer than 40% of content roles have any assets. This hub needs significant work before output generation is possible.",
  partial: "40–79% of content roles have at least one asset. Some roles are still missing — fill the gaps to reach output readiness.",
  ready: "80–99% of content roles have at least one asset. Almost fully covered — just a few roles left to fill.",
  strong: "All content roles have at least one asset. This hub is fully covered and output-ready.",
};

export const SEQUENCE_STATUS_TOOLTIPS: Record<SequenceStatus, string> = {
  sequence_ready: "Before, during, and after assets are all present. Full sequence outputs are possible.",
  before_after_available: "Before and after assets exist but during is missing. Add in-progress images to unlock full sequence capability.",
  missing_before: "After assets exist but before assets are missing. Add pre-work images to complete the sequence.",
  missing_after: "Before assets exist but after assets are missing. Add post-repair images to complete the sequence.",
  no_sequence: "No temporal coverage yet. Promote assets with before, during, or after classifications.",
};

export const BUCKET_STATUS_LABELS: Record<BucketStatus, string> = {
  empty: "Empty",
  partial: "Partial",
  ready: "Ready",
  strong: "Strong",
};

// Strength statuses: empty=grey, partial=yellow, ready=blue, strong=green
export const PROBLEM_HUB_STATUS_BADGE_CLASSES: Record<BucketStatus, string> = {
  empty: "!bg-muted/50 !text-muted-foreground",
  partial: "!bg-amber-500/15 !text-amber-500",
  ready: "!bg-primary/15 !text-primary",
  strong: "!bg-success/15 !text-success",
};

// Sequence statuses: sequence_ready=green, before_after_available=blue,
// missing_before/after=yellow, no_sequence=grey
export const SEQUENCE_STATUS_BADGE_CLASSES: Record<SequenceStatus, string> = {
  sequence_ready: "!bg-success/15 !text-success",
  before_after_available: "!bg-primary/15 !text-primary",
  missing_before: "!bg-amber-500/15 !text-amber-500",
  missing_after: "!bg-amber-500/15 !text-amber-500",
  no_sequence: "!bg-muted/50 !text-muted-foreground",
};

// Sequence completeness score (0–100) used for composite progress calculation.
// sequence_ready=all 3 phases, before_after_available=2/3, missing one key phase=1/3, no_sequence=0
export const SEQUENCE_STATUS_SCORE: Record<SequenceStatus, number> = {
  sequence_ready: 100,
  before_after_available: 67,
  missing_before: 33,
  missing_after: 33,
  no_sequence: 0,
};

export const SEQUENCE_STATUS_LABELS: Record<SequenceStatus, string> = {
  sequence_ready: "Sequence ready",
  before_after_available: "Before / After",
  missing_before: "Missing before",
  missing_after: "Missing after",
  no_sequence: "No sequence",
};

export const COVERAGE_KPI_CONTEXT_BADGE_CLASSES = {
  landscape_ready: "!bg-success/15 !text-success",
  sequence_capable: "!bg-primary/15 !text-primary",
  blind_spot_attention: "!bg-destructive/15 !text-destructive",
  blind_spot_clear: "!bg-success/15 !text-success",
} as const;

export function problemHubStatusBadgeClass(status: BucketStatus): string {
  const fragment = PROBLEM_HUB_STATUS_BADGE_CLASSES[status];
  return cn(COVERAGE_BADGE_BASE, fragment ?? PROBLEM_HUB_STATUS_BADGE_CLASSES.empty);
}

export function sequenceBadgeClassFromStatus(status: SequenceStatus): string {
  const fragment = SEQUENCE_STATUS_BADGE_CLASSES[status];
  return cn(COVERAGE_BADGE_BASE, fragment ?? SEQUENCE_STATUS_BADGE_CLASSES.no_sequence);
}

// ---------------------------------------------------------------------------
// Hub row — progress & color helpers
// ---------------------------------------------------------------------------

export const TEMPORAL_PHASES = ["before", "during", "after"] as const;
export type TemporalPhase = typeof TEMPORAL_PHASES[number];

// Composite score: content fill (70%) + sequence completeness (30%).
// A hub with full content but broken sequence visibly scores lower.
export function getHubProgress(hub: ProblemHub): number {
  const contentPct =
    hub.buckets.length === 0
      ? 0
      : (hub.buckets.filter((b) => b.assetCount > 0).length / hub.buckets.length) * 100;
  const seqPct = SEQUENCE_STATUS_SCORE[hub.sequenceStatus] ?? 0;
  return Math.round(contentPct * 0.7 + seqPct * 0.3);
}

// Color gated by sequence: strong+sequence_ready=green, strong/ready=blue,
// partial=amber, empty=muted (never red).
export function getHubProgressBarClass(hub: ProblemHub): string {
  if (hub.overallStatus === "strong" && hub.sequenceStatus === "sequence_ready") return "bg-success";
  if (hub.overallStatus === "strong" || hub.overallStatus === "ready") return "bg-primary";
  if (hub.overallStatus === "partial") return "bg-amber-500";
  return "bg-muted-foreground/50";
}

export function getHubProgressTextClass(hub: ProblemHub): string {
  if (hub.overallStatus === "strong" && hub.sequenceStatus === "sequence_ready") return "text-success";
  if (hub.overallStatus === "strong" || hub.overallStatus === "ready") return "text-primary";
  if (hub.overallStatus === "partial") return "text-amber-500";
  return "text-foreground/50";
}

export function kpiContextBadgeClass(
  key: keyof typeof COVERAGE_KPI_CONTEXT_BADGE_CLASSES,
  layoutClass?: string,
): string {
  return cn(COVERAGE_BADGE_BASE, COVERAGE_KPI_CONTEXT_BADGE_CLASSES[key], layoutClass);
}
